import { useEffect } from 'react';
import type { SaveStatus } from '../components/app-home-v2/PreferencesDialog';
import type { useLayoutPrefs } from './useLayoutPrefs';

const SAVED_INDICATOR_MS = 1500;
const ERROR_INDICATOR_MS = 3000;

/**
 * Maps one `useLayoutPrefs` instance's raw mutation status to a Preferences
 * row `SaveStatus` and auto-resets 'saved'/'error' after the indicator
 * timeout, for a thin write hook over `useLayoutPrefs` whose caller shows a
 * Preferences-row save indicator (today `useSetTerraDesignV2`).
 *
 * Derives purely from the mutation's own status — no local `useState`/
 * `useRef`. The auto-reset effect has a cleanup, so it is StrictMode-safe by
 * construction and cannot fire after unmount.
 */
export function usePrefSaveStatus(
  mutationStatus: ReturnType<typeof useLayoutPrefs>['saveStatus'],
  resetSaveStatus: () => void,
): SaveStatus | undefined {
  const saveStatus: SaveStatus | undefined =
    mutationStatus === 'pending' ? 'saving'
    : mutationStatus === 'success' ? 'saved'
    : mutationStatus === 'error' ? 'error'
    : undefined;

  useEffect(() => {
    if (saveStatus !== 'saved' && saveStatus !== 'error') return;
    const ms = saveStatus === 'saved' ? SAVED_INDICATOR_MS : ERROR_INDICATOR_MS;
    const timer = setTimeout(resetSaveStatus, ms);
    return () => clearTimeout(timer);
  }, [saveStatus, resetSaveStatus]);

  return saveStatus;
}
